import {useEffect} from 'react';
import {Controller, ControllerProps, useFormContext} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {Form, CustomDatePicker} from '@innowise-group/ui-kit';
import {WorkspaceBookType, WorkspaceStatuses} from '@innowise-group/core';
import {ExcludeDates} from '@shared-components';
import {SelectStatusFormFields} from '../workspace-status-form';
import {isCoworking, validateDate} from '@shared/utilities';

export enum WorkspaceBookingDateFormFields {
  DateRange = 'dateRange',
}

export interface WorkspaceBookingDateFormValues {
  dateRange: [Date, Date];
}

const WorkspaceBookingDateForm: React.FC<
  Omit<ControllerProps, 'render' | 'name'> & {status?: WorkspaceBookType; datesToExclude?: ExcludeDates}
> = ({defaultValue, status, datesToExclude, ...props}) => {
  const {
    control,
    formState: {errors},
    watch,
    trigger,
  } = useFormContext();

  const {t} = useTranslation();

  const watchedStatus = watch(SelectStatusFormFields.WorkspaceStatus, status);
  const checkIfRequired = () => {
    return watchedStatus === WorkspaceStatuses.Booked || watchedStatus === WorkspaceStatuses.CoworkingBooked;
  };

  useEffect(() => {
    trigger(WorkspaceBookingDateFormFields.DateRange);
  }, [trigger, watchedStatus]);

  return (
    <Form.Layout layoutType="horizontal">
      <Controller
        {...props}
        name={WorkspaceBookingDateFormFields.DateRange}
        defaultValue={[
          defaultValue?.startDay ? new Date(defaultValue.startDay) : undefined,
          defaultValue?.endDay ? new Date(defaultValue.endDay) : undefined,
        ]}
        control={control}
        rules={{
          validate: (value) => {
            if (!checkIfRequired()) return true;
            if (!value?.every(Boolean)) return t('modals.messages.required');
            if (isCoworking(watchedStatus) && !validateDate.isCoworkingRangeValid(value)) {
              return t('modals.messages.maxCoworkingBookingRange');
            }
            return true;
          },
        }}
        render={({field: {onChange, value}}) => (
          <Form.Field
            label={`${t('modals.bookingStart')} / ${t('modals.bookingEnd')} ${checkIfRequired() ? '*' : ''}`}
            error={errors[WorkspaceBookingDateFormFields.DateRange]?.message}
          >
            <CustomDatePicker
              selectsRange
              selected={watch(WorkspaceBookingDateFormFields.DateRange)?.[0]}
              startDate={watch(WorkspaceBookingDateFormFields.DateRange)?.[0]}
              endDate={watch(WorkspaceBookingDateFormFields.DateRange)?.[1]}
              onChange={onChange}
              dateFormat="dd/MM/yyyy"
              placeholderText={t('placeholders.datePicker')}
              minDate={new Date()}
              maxDate={validateDate.maxInRange(
                watch(WorkspaceBookingDateFormFields.DateRange),
                isCoworking(watchedStatus),
              )}
              excludeDateIntervals={datesToExclude?.map((status) => status.range)}
              disabled={!checkIfRequired()}
              popperPlacement="top"
              isClearable={true}
            />
          </Form.Field>
        )}
      />
    </Form.Layout>
  );
};

export default WorkspaceBookingDateForm;
